'use client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useT } from '../../lib/i18n';
import type { CategoryTree, ListStyleBlock, ProductListItem, ProductListPage } from '../../lib/site';
import { Icon } from '../Icon';
import { InfiniteSentinel } from '../InfiniteRows';
import { CourseCard } from './CourseCard';
import { CourseRow } from './CourseRow';
import { SkeletonCards, SkeletonRows } from './ListSkeleton';
import { CourseFilters, type FilterState } from './CourseFilters';
import { productHref } from './href';
import { type ListPrefs, prefsFromStyle, prefsToQuery, writePrefs } from './listPrefs';

/**
 * 강좌·교재 목록 — **한 모듈이 모든 자리를 맡는다**(2026-08-22 사용자 지시).
 *
 * 현행은 `template/product_list_inc.php` 하나가 강좌 목록·교재 목록·상세의 포함 강좌·
 * 연관 교재·필터 AJAX·강사 상세·찜 목록·메인 구성·프로모션 `{lecture_list}` 토큰까지
 * 아홉 자리를 그렸다. 새 프론트도 여기 하나로 모은다 — 카드가 두 벌이 되는 순간
 * 찜·가격 표기가 어긋난다.
 *
 * 화면별 차이는 **props 로만** 낸다. 목록을 다시 짜는 화면이 있으면 안 된다.
 *
 * 그리는 형태는 설정이 정한다(§④-a) —
 * `style`(뷰 기본·자세히/간단·필터 노출·페이지네이션·개수·초기 정렬)·`priceDetail`·분류 단계.
 * 회원이 바꾼 뷰·정렬은 `storageKey` 별로 기억한다(§④-c).
 */
export function CourseList({
  storageKey,
  style,
  priceDetail,
  categories = null,
  initial,
  baseQuery = {},
  detailBase = '/lectures',
  showFilters = true,
  forceView,
  initialPrefs,
  initialQ,
  searchPlaceholder,
  subscribed = false,
  initialBilling = false,
}: {
  /**
   * 이 자리의 이름 — 뷰·정렬 기억이 자리마다 갈린다(`lectures`·`books`·`teacher:3` …).
   * `forceView` 를 주면 기억하지 않는다.
   */
  storageKey: string;
  style: ListStyleBlock;
  priceDetail: boolean;
  /** 분류 트리 — 필터를 안 쓰는 자리는 넘기지 않는다 */
  categories?: CategoryTree | null;
  /** SSR 로 받아 온 첫 페이지 — 첫 페인트는 이것으로 그린다 */
  initial: ProductListPage;
  /** 이 자리의 고정 조건 — 예: 교재 목록은 `{ type: 'BOOK' }`, 강사 상세는 `{ teacherId }` */
  baseQuery?: Record<string, string | number>;
  /** 상세 링크의 앞부분 — 교재는 `/books` */
  detailBase?: string;
  /** 필터줄 노출 — 상세의 연관상품처럼 목록만 필요한 자리는 false(현행 `sub_index`) */
  showFilters?: boolean;
  /** 뷰 고정 — 찜 목록처럼 현행이 형태를 못 박은 자리(기억하지 않는다) */
  forceView?: 'card' | 'list';
  /** 서버가 쿠키에서 읽어 준 첫 취향 — 없으면 설정 기본값(그때는 깜박임 없이 기본형이다) */
  initialPrefs?: ListPrefs | null;
  /** 첫 검색어 — 명령 팔레트의 Enter(/lectures?q=)가 이리로 온다 */
  initialQ?: string;
  searchPlaceholder?: string;
  /**
   * 이 회원이 구독 중인가 — 참일 때만 [전체 보기]/[구독 보기] 토글을 그린다
   * (현행 `product_list_inc.php:123` 도 `billing_yn == 'Y'` 일 때만 그렸다).
   * 구독을 안 하는 회원에게 보여 주면 눌러도 빈 목록이라 고장으로 읽힌다.
   */
  subscribed?: boolean;
  /** 구독 보기로 열렸는가 — `/lectures?billing=1`(나의 강의실 [수강 강좌 보기]가 이리로 온다) */
  initialBilling?: boolean;
}) {
  const t = useT();
  const [prefs, setPrefs] = useState<ListPrefs>(() => initialPrefs ?? prefsFromStyle(style));
  const [filters, setFilters] = useState<FilterState>({ type: null, categoryPath: [], badge: null, q: initialQ ?? '' });
  /*
   * 구독 보기(현행 `/goods/lecture/billing`) — 목록 조건이자 카드 모양이다.
   * 취향(`prefs`)에 넣지 않는다: 구독이 끝나면 뜻을 잃는 값이라 쿠키에 남으면
   * 다음에 들어와 빈 목록을 본다. 화면을 나가면 잊는다.
   */
  const [billing, setBilling] = useState(initialBilling && subscribed);
  const [page, setPage] = useState(initial.page);
  const [data, setData] = useState(initial);
  const [loading, setLoading] = useState(false);
  /** 첫 렌더는 SSR 결과를 그대로 쓴다 — 마운트 직후의 불필요한 재조회를 막는다 */
  const primed = useRef(false);

  const view = forceView ?? prefs.view;
  /*
   * 페이지네이션을 끈 마켓은 **무한 스크롤**이다(2026-08-22 UX 채택안 #14 — 프론트만,
   * 관리자 설정 존중: 켠 마켓은 페이저 그대로). 그래서 끈 마켓도 한 묶음(perPage)씩 받고,
   * 바닥 근처에 오면 다음 묶음을 **이어 붙인다**(전에는 한 번에 200개를 받았다).
   */
  const infinite = !style.pagination;
  const size = style.perPage;

  /*
   * 부모가 넘긴 고정 조건은 렌더마다 새 객체다 — 그대로 의존성에 넣으면 무한히 다시 조회한다.
   * 값이 같으면 같은 문자열이 되도록 굳혀서 쓴다.
   */
  const baseKey = JSON.stringify(baseQuery);

  /*
   * **뷰(카드/리스트)는 조회 조건이 아니다**(2026-08-26 사용자 제보).
   *
   * `prefs` 를 통째로 의존성에 넣었더니 **뷰만 바꿔도 다시 받았다** — 무한 스크롤로
   * 끝까지 내려 쌓아 둔 목록이 지금 쪽(마지막 8개) 하나로 덮였다.
   * 뷰는 같은 데이터를 다르게 그리는 것뿐이라 서버에 물을 것이 없다
   * (`prefsToQuery` 도 `view` 를 쓰지 않는다). 정렬만 열쇠로 삼는다.
   */
  const sortKey = `${prefs.sort}:${prefs.dir}`;
  /*
   * `style` 은 **객체 prop** 이라 서버가 다시 그릴 때마다 새 것이 온다(같은 값이어도).
   * 그대로 의존성에 두면 **같은 메뉴를 다시 눌렀을 때도 재조회**가 일어난다
   * (2026-08-26 사용자 제보). `baseQuery` 와 같은 방식으로 값으로 굳힌다.
   */
  const styleKey = JSON.stringify(style);
  /** 구독 보기는 조회 조건이다 — 켜고 끌 때 다시 받아야 한다 */
  const billingKey = billing;

  /** 서버에 다시 묻기 — 필터·정렬·페이지가 바뀔 때만. 무한 스크롤은 이어 붙인다 */
  const reload = useCallback(async (append = false) => {
    const q = new URLSearchParams({
      ...Object.fromEntries(Object.entries(JSON.parse(baseKey) as Record<string, unknown>).map(([k, v]) => [k, String(v)])),
      ...prefsToQuery(prefs, style),
      page: String(page),
      size: String(size),
    });
    if (billingKey) q.set('billing', '1');
    if (filters.type) q.set('type', filters.type);
    if (filters.badge) q.set('badge', filters.badge);
    if (filters.q) q.set('q', filters.q);
    // 가장 깊게 고른 분류 하나만 보낸다 — 하위를 고르면 상위는 자동으로 포함된다
    const picked = [...filters.categoryPath].reverse().find((x): x is number => x != null);
    if (picked) q.set('categoryId', String(picked));

    setLoading(true);
    setAppending(append);
    try {
      const res = await fetch(`/api/products?${q.toString()}`);
      if (res.ok) {
        const next = (await res.json()) as ProductListPage;
        /*
         * **끝을 여기서 못박는다**(2026-08-26 사용자 제보 "휠 한 번에 200쪽을 받는다").
         * 전에는 `rows.length >= total` 로만 판단했는데, 감시선이 조회보다 빨리 되먹임하면
         * 그 수가 따라오기 전에 다음 쪽을 계속 청한다. **받은 줄이 한 묶음보다 적으면
         * 그것이 마지막**이다 — 서버에 더 물을 것이 없다.
         */
        if (next.rows.length < size) atEnd.current = true;
        setData((cur) => (append ? { ...next, rows: [...cur.rows, ...next.rows] } : next));
      }
    } catch {
      // 조회 실패는 직전 목록을 그대로 둔다 — 화면이 비어 버리는 것보다 낫다
    } finally {
      setLoading(false);
      setAppending(false);
      asking.current = false; // 이 요청이 끝나야 다음 쪽을 청할 수 있다
    }
    // `prefs` 대신 `sortKey` 를 본다 — 뷰가 바뀌었다고 다시 받지 않는다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseKey, sortKey, styleKey, page, size, filters, billingKey]);

  /*
   * 무한 스크롤의 페이지 넘김과 필터 변경을 갈라야 한다 —
   * 페이지가 올라간 것이면 이어 붙이고, 필터·정렬이 바뀐 것이면 처음부터 다시 받는다.
   */
  const appendNext = useRef(false);
  /** 지금 한 묶음을 받는 중인가 — 감시선이 여러 번 울려도 한 번만 청한다 */
  const asking = useRef(false);
  /**
   * 지금이 **이어 받는 중**인가 — 첫 조회와 갈라야 한다.
   * 첫 조회는 화면 전체가 뼈대(`loading.tsx`)이고, 이어 받기는 **이미 있는 목록 뒤에**
   * 다음 묶음 자리를 뼈대로 미리 깔아 준다(2026-08-26 사용자 제안).
   */
  const [appending, setAppending] = useState(false);
  /** 마지막 묶음까지 받았나 — 한 번 참이 되면 필터가 바뀌기 전까지 더 청하지 않는다 */
  const atEnd = useRef(false);

  useEffect(() => {
    if (!primed.current) { primed.current = true; return; }
    const append = appendNext.current;
    appendNext.current = false;
    if (!append) {
      atEnd.current = false; // 조건이 바뀌면 처음부터 다시 — 끝 표시를 푼다
      /*
       * **이어 붙이는 게 아니면 첫 쪽부터**(2026-08-26 사용자 제보).
       *
       * 무한 스크롤은 쌓아 둔 목록이 곧 화면이다. 그런데 쪽 번호는 마지막(예: 9)에 머물러
       * 있어서, 조건이 바뀌거나 같은 메뉴를 다시 눌러 새로 받으면 **그 마지막 쪽만** 받아
       * 목록이 8개로 줄었다. 더 눌러 쪽이 넘어가 있으면 아예 "강좌가 없습니다" 가 됐다.
       * 쪽을 되돌리면 이 효과가 다시 돌아 첫 쪽을 받는다 — 여기서는 물러난다.
       */
      if (infinite && page !== 1) { setPage(1); return; }
    }
    void reload(append);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  /*
   * 다음 묶음 청하기 — **함수를 고정한다**(`useCallback`).
   * 렌더마다 새 함수를 넘기면 감시선의 효과가 매번 다시 돌고, 관측을 새로 걸 때
   * `IntersectionObserver` 가 **지금 보이는지를 즉시 알려** 곧바로 또 청하게 된다.
   * 그 되먹임이 조회보다 빨라 휠 한 번에 수백 쪽을 받았다(2026-08-26 제보).
   * 받는 중이거나 끝났으면 청하지 않는다.
   */
  const askMore = useCallback(() => {
    if (asking.current || atEnd.current) return;
    asking.current = true;
    appendNext.current = true;
    setPage((p) => p + 1);
  }, []);

  const changePrefs = (next: ListPrefs) => {
    setPrefs(next);
    if (!forceView) writePrefs(storageKey, next);
    // 정렬이 바뀌면 첫 페이지로 — 3페이지에서 정렬을 바꾸면 엉뚱한 구간을 본다
    if (next.sort !== prefs.sort || next.dir !== prefs.dir) setPage(1);
  };

  const changeFilters = (next: FilterState) => { setFilters(next); setPage(1); };

  /**
   * 전체/구독 갈아타기 — 조건이 바뀌므로 첫 쪽부터 다시 받는다.
   * 끝 표시(`atEnd`)는 위 효과가 이어 받기가 아닐 때 알아서 푼다(`changeFilters` 와 같다).
   */
  const pickBilling = (next: boolean) => {
    if (next === billing) return;
    setBilling(next);
    setPage(1);
  };

  const cardVariant = style.cardVariant;
  const listVariant = style.listVariant;
  const totalPages = style.pagination ? Math.max(1, Math.ceil(data.total / size)) : 1;

  return (
    <>
      {showFilters && (
        <CourseFilters style={style} categories={categories} filters={filters} prefs={prefs}
          onFilters={changeFilters} onPrefs={changePrefs}
          searchPlaceholder={searchPlaceholder ?? t('prd.searchPh')}
          controlsLeft={subscribed ? (
            /* 구독 보기 토글 — 정렬 탭과 같은 부품(`sort-tabs`)을 쓴다. 가이드에 라디오 타일이
               없고, 같은 줄에서 두 벌의 모양이 서로 다르면 어느 쪽이 눌린 건지 읽기 어렵다 */
            <div className="sort-tabs">
              <button className={`sort-tab${billing ? '' : ' active'}`} onClick={() => pickBilling(false)}>
                <Icon name="layout-grid" />{t('prd.billing.all')}
              </button>
              <button className={`sort-tab${billing ? ' active' : ''}`} onClick={() => pickBilling(true)}>
                <Icon name="package" />{t('prd.billing.mine')}
              </button>
            </div>
          ) : undefined} />
      )}

      {data.rows.length === 0 && !loading && (
        <div className="cp-empty"><Icon name="search-x" />
          {/* 구독 보기에서 비었으면 검색 결과가 없는 게 아니라 **범위에 강좌가 없는** 것이다 */}
          <p>{billing ? t('prd.billing.empty') : t('prd.empty')}</p>
        </div>
      )}

      {/* 카드형 / 리스트형 — 뷰 하나만 그린다(가이드는 견본으로 둘을 나란히 뒀다) */}
      {data.rows.length > 0 && (view === 'card' ? (
        <div className={`course-grid mb20${cardVariant === 'simple' ? ' simple-grid' : ''}`}
          style={loading && !appending ? { opacity: 0.5 } : undefined}>
          {data.rows.map((p) => (
            <CourseCard key={p.id} p={p} variant={cardVariant} priceDetail={priceDetail}
              freeVod={style.freeVod} hoverPreview={style.hoverPreview} billing={billing}
              href={productHref(detailBase, p)} />
          ))}
          {/* 다음 묶음 자리 — 받는 동안 뼈대가 먼저 서서 화면이 튀지 않는다 */}
          {appending && <SkeletonCards n={size} />}
        </div>
      ) : (
        <div className={`course-list mb20${listVariant === 'simple' ? ' simple-list' : ''}`}
          style={loading && !appending ? { opacity: 0.5 } : undefined}>
          {data.rows.map((p) => (
            <CourseRow key={p.id} p={p} variant={listVariant} priceDetail={priceDetail}
              freeVod={style.freeVod} hoverPreview={style.hoverPreview} billing={billing}
              href={productHref(detailBase, p)} />
          ))}
          {appending && <SkeletonRows n={size} />}
        </div>
      ))}

      {/* 무한 스크롤(페이지네이션을 끈 마켓) — 바닥 근처에서 다음 묶음을 이어 받는다 */}
      {infinite && (
        <InfiniteSentinel
          done={atEnd.current || data.rows.length >= data.total}
          loading={loading}
          more={askMore}
        />
      )}

      {/* 페이지네이션 — 설정에서 끈 마켓은 없다(현행 pagination).
          가이드는 `d-flex jc-center` 로 **가운데** 둔다(`.pagination` 자체엔 정렬이 없다) */}
      {style.pagination && totalPages > 1 && (
        <div className="d-flex jc-center mt25">
        <div className="pagination">
          <button className="page-btn" disabled={page <= 1} onClick={() => setPage(page - 1)}
            aria-label={t('prd.prevPage')}><Icon name="chevron-left" /></button>
          {pageNumbers(page, totalPages).map((n) => (
            <button key={n} className={`page-btn${n === page ? ' active' : ''}`} onClick={() => setPage(n)}>{n}</button>
          ))}
          <button className="page-btn" disabled={page >= totalPages} onClick={() => setPage(page + 1)}
            aria-label={t('prd.nextPage')}><Icon name="chevron-right" /></button>
        </div>
        </div>
      )}
    </>
  );
}

/** 상세 링크 — 교재는 종류가 섞여 들어와도 교재 화면으로 보낸다(현행도 ptype 으로 갈랐다) */
/** 보여 줄 페이지 번호 — 현재 페이지 주변 다섯 칸(가이드 페이저가 좁다) */
function pageNumbers(page: number, total: number): number[] {
  const start = Math.max(1, Math.min(page - 2, total - 4));
  const end = Math.min(total, start + 4);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}
